import { byPrefixAndName } from '@awesome.me/kit-39de79544e/icons'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, styled } from '@mui/material'
import dayjs from 'dayjs'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCampaignRecepients } from '@/api/campaigns/getRecepients'
import { US_DATE_WITH_MONTH_NAME } from '@/api/constants'
import LayoutAccordion from '@/common/components/Layout/LayoutPageContent/LayoutAccordion'
import SimpleSearchBar from '@/common/components/SimpleSearchBar/SimpleSearchBar'
import Table from '@/common/components/Table'
import Tags from '@/common/components/Tags/Tags'
import useQueryParams from '@/common/hooks/useQueryParams'
import { theme } from '@/theme/theme'
import { CampaignRecipient } from '@/types/campaigns'

type CampaignMembersProps = {
  campaignId: string
}

const INITIAL_QUERY_FILTERS = {
  limit: 10,
  skip: 0,
  search: '',
}

const CampaignMembers: FC<CampaignMembersProps> = ({ campaignId }) => {
  const [expanded, setExpanded] = useState(true)

  const { t } = useTranslation()

  const { queryParams, updateQueryParam } = useQueryParams(INITIAL_QUERY_FILTERS)

  const { data: recepientsData, isFetching } = useCampaignRecepients(campaignId, queryParams)
  const { results: recipients, total } = recepientsData || { total: 0, results: [] }

  const tableColumns = useMemo(
    () => [
      {
        id: 'full_name',
        label: t('campaignDashboard.campaignMembers.name'),
      },
      {
        id: 'username',
        label: t('campaignDashboard.campaignMembers.email'),
      },
      {
        id: 'phone_number',
        label: t('campaignDashboard.campaignMembers.phone'),
        component: (row: CampaignRecipient) =>
          row.phone_number ? (
            <>{row.phone_number}</>
          ) : (
            <StyledFontAwesome icon={byPrefixAndName.fak['light-mobile-circle-xmark'] as IconProp} />
          ),
      },
      {
        id: 'member_of',
        label: t('campaignDashboard.campaignMembers.groups'),
        component: (row: CampaignRecipient) => (
          <Tags
            justify="center"
            useChips={false}
            tags={(row.member_of ?? []).map((group: string) => ({
              label: group,
            }))}
            limit={1}
          />
        ),
      },
      {
        id: 'campaign_score',
        label: t('campaignDashboard.campaignMembers.score'),
        format: (row: CampaignRecipient) => (row.campaign_score === -1 || !row.delivered_at ? '-' : row.campaign_score),
      },
      {
        id: 'delivered_at',
        label: t('campaignDashboard.campaignMembers.deliveredAt'),

        format: (row: CampaignRecipient) =>
          row.delivered_at ? dayjs(row.delivered_at).format(US_DATE_WITH_MONTH_NAME) : '-',
      },
      {
        id: 'status',
        label: t('campaignDashboard.campaignMembers.status'),
        format: (row: CampaignRecipient) => row.status.charAt(0).toUpperCase() + row.status.slice(1),
      },
    ],
    []
  )

  return (
    <LayoutAccordion
      title={t('campaignDashboard.campaignMembers.title')}
      expanded={expanded}
      onClick={() => setExpanded(!expanded)}>
      <Box mb={2}>
        <SimpleSearchBar
          value={queryParams.search}
          onChange={(value) => {
            updateQueryParam({ search: value })
          }}
          debounceTime={300}
        />
      </Box>
      <Table
        loading={isFetching}
        data={recipients}
        total={total}
        queryParams={queryParams}
        updateQueryParam={updateQueryParam}
        columns={tableColumns}
      />
    </LayoutAccordion>
  )
}

const StyledFontAwesome = styled(FontAwesomeIcon)({
  color: theme.palette.orange[900],
  height: 18,
  display: 'inline-flex',
  alignItems: 'center',
  margin: 'auto',
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
})

export default CampaignMembers
